import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getSafetyRepNotifications,
  getSafetyRepUnread,
  getSafetyRepWork,
  markNotificationsRead,
  NotificationPreferences,
  returnDocumentation,
  saveNotificationPreferences,
  signDocumentation,
  undoDocumentationAnswer,
} from '../../../api/safetyRepWork';

export const SafetyRepWorkQueryKey = 'SAFETY_REP_WORK';
export const SafetyRepNotificationsQueryKey = 'SAFETY_REP_NOTIFICATIONS';

/** The menu counts the tasks too, so it asks only with «Mine oppgaver» in the menu */
export const useSafetyRepWorkQuery = (language: string, enabled = true) =>
  useQuery({
    queryKey: [SafetyRepWorkQueryKey, language],
    queryFn: () => getSafetyRepWork(language),
    enabled,
  });

export const useSafetyRepNotificationsQuery = () =>
  useQuery({
    queryKey: [SafetyRepNotificationsQueryKey],
    queryFn: getSafetyRepNotifications,
  });

const UNREAD_KEY = [SafetyRepNotificationsQueryKey, 'unread'];
const UNREAD_INTERVAL = 60_000;

/**
 * How many notifications wait for the safety representative, for the menu. A task done anywhere in the app can read
 * notifications, so the count is fetched again after every change, and once a minute.
 */
export const useSafetyRepUnreadQuery = (enabled: boolean) => {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    return queryClient.getMutationCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'success')
        void queryClient.invalidateQueries({ queryKey: UNREAD_KEY });
    });
  }, [enabled, queryClient]);
  return useQuery({
    queryKey: UNREAD_KEY,
    queryFn: getSafetyRepUnread,
    enabled,
    refetchInterval: UNREAD_INTERVAL,
    refetchOnWindowFocus: true,
    // The menu counts what waits, not what only tells what happened
    select: (data) => data.waiting,
  });
};

/** Every answer changes what waits for the safety representative, so their pages are fetched again */
const useWorkMutation = <T>(run: (input: T) => Promise<void>) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () =>
      Promise.all(
        [SafetyRepWorkQueryKey, SafetyRepNotificationsQueryKey].map((key) =>
          queryClient.invalidateQueries({ queryKey: [key] }),
        ),
      ),
  });
};

export const useMarkNotificationsReadMutation = () =>
  useWorkMutation((id: number | null) => markNotificationsRead(id));

export const useSaveNotificationPreferencesMutation = () =>
  useWorkMutation((preferences: NotificationPreferences) =>
    saveNotificationPreferences(preferences),
  );

export const useSignDocumentationMutation = () =>
  useWorkMutation((surveyId: number) => signDocumentation(surveyId));

export const useReturnDocumentationMutation = () =>
  useWorkMutation(
    ({ surveyId, comment }: { surveyId: number; comment: string }) =>
      returnDocumentation(surveyId, comment),
  );

export const useUndoDocumentationAnswerMutation = () =>
  useWorkMutation((surveyId: number) => undoDocumentationAnswer(surveyId));
